/**
 * PHOTO UPLOAD SERVICE
 * 
 * Centralized photo upload with automatic storage limit checking
 * and DMCA-compliant source tracking.
 * 
 * Features:
 * - ✅ Checks storage limits BEFORE upload
 * - ✅ Compresses images automatically
 * - ✅ Tracks storage usage after upload
 * - ✅ Tracks photo source/provenance for DMCA
 * - ✅ Returns same Photo format as before
 * - ✅ Handles errors gracefully
 * 
 * Usage:
 * ```typescript
 * import { uploadPhoto, uploadPhotos, checkStorageBeforeUpload } from '../services/photoUploadService';
 * 
 * // Single photo with source tracking
 * const result = await uploadPhoto(userId, file, 'polishes', {
 *   source: 'original',      // User took the photo
 *   creditName: 'Jane Doe',  // Optional credit
 *   creditUrl: 'https://...' // Optional link
 * });
 * 
 * // Photo from web
 * const result = await uploadPhoto(userId, file, 'polishes', {
 *   source: 'web',
 *   sourceUrl: 'https://instagram.com/...'
 * });
 * ```
 */

import { ref, uploadBytes, getDownloadURL, getMetadata, deleteObject } from 'firebase/storage';
import { storage } from '../firebase/config';
import { compressImage } from '../utils/imageUtils';
import {
  addToStorageUsage,
  checkCanUpload,
  subtractFromStorageUsage
} from './storageService';
import type { Photo } from '../types';
import type { PhotoSource, DMCAStatus } from '../types/dmca';

// Input format for pending photos (matches existing modal pattern)
export interface PendingPhoto {
  id: string;
  file: File;
  preview: string;
  caption?: string;
  creditName?: string;
  creditUrl?: string;
  isDefault?: boolean;
  // New source tracking fields
  source?: PhotoSource;
  sourceUrl?: string;
  sourceUserId?: string;
  /** Optional per-photo context — see `Photo.skinTone`/`Photo.lighting`
   *  in `types/index.ts`. Round-trips through the Add[Type] modal caption
   *  card down to the final Photo doc. */
  skinTone?: string;
  lighting?: string;
}

// Extended Photo type with provenance tracking
export interface PhotoWithProvenance extends Photo {
  uploadedBy?: string;
  uploadedAt?: string;
  source?: PhotoSource;
  sourceUrl?: string;
  sourceUserId?: string;
  dmcaStatus?: DMCAStatus;
  dmcaClaimId?: string;
}

// Result of a single photo upload
export interface PhotoUploadResult {
  success: boolean;
  photo?: PhotoWithProvenance;
  error?: string;
  bytesUsed?: number;
  showUpgradeModal?: boolean;  // True if blocked due to storage limit
}

// Result of bulk photo upload
export interface BulkUploadResult {
  success: boolean;
  photos: PhotoWithProvenance[];
  errors: string[];
  totalBytesUsed: number;
  failedCount: number;
  storageBlocked?: boolean;  // True if any uploads were blocked due to storage
}

// Storage check result (for pre-upload validation)
export interface StorageCheckResult {
  canUpload: boolean;
  reason?: string;
  currentUsageBytes: number;
  limitBytes: number;
  percentUsed: number;
  showUpgradeModal?: boolean;
}

// Upload options including source tracking
export interface PhotoUploadOptions {
  caption?: string;
  creditName?: string;
  creditUrl?: string;
  isDefault?: boolean;
  skipStorageCheck?: boolean;  // For admin/system uploads
  // Source tracking for DMCA
  source?: PhotoSource;
  sourceUrl?: string;          // Original URL if from web/brand
  sourceUserId?: string;       // Original user if from marketplace
  /** True when the caller has confirmed the user explicitly checked the
   *  "I have the right to share this image" attestation. Only true-flagged
   *  photos become eligible for tile-representative use via
   *  `Polish.communityCoverPhotoUrl` in the catalog. Absent/false leaves
   *  the photo at its current visibility (own item + Photos from Collectors). */
  attested?: boolean;
  /** Optional per-photo context — see `Photo.skinTone`/`Photo.lighting`
   *  in `types/index.ts`. */
  skinTone?: string;
  lighting?: string;
}

/**
 * Check if user can upload files of given total size
 * Call this BEFORE starting uploads to give early feedback
 */
export async function checkStorageBeforeUpload(
  userId: string,
  totalSizeBytes: number
): Promise<StorageCheckResult> {
  try {
    const result = await checkCanUpload(userId, totalSizeBytes);
    
    return {
      canUpload: result.allowed,
      reason: result.reason,
      currentUsageBytes: result.currentUsageBytes,
      limitBytes: result.limitBytes,
      percentUsed: result.percentUsed,
      showUpgradeModal: !result.allowed,
    };
  } catch (error) {
    console.error('Error checking storage:', error);
    // On error, allow upload (fail open) - we'll catch it during actual upload
    return {
      canUpload: true,
      currentUsageBytes: 0,
      limitBytes: 0,
      percentUsed: 0,
    };
  }
}

/**
 * Estimate total upload size from files
 * Useful for checking storage before upload
 */
export function estimateUploadSize(files: File[]): number {
  // After compression, images are typically 10-30% of original size
  // Use 25% as a conservative estimate
  const COMPRESSION_RATIO = 0.25;
  
  const totalOriginalSize = files.reduce((sum, file) => sum + file.size, 0);
  return Math.ceil(totalOriginalSize * COMPRESSION_RATIO);
}

/**
 * Upload a single photo with storage checking and source tracking
 * 
 * @param userId - The user's ID
 * @param file - The file to upload
 * @param folder - Storage folder (e.g., 'polishes', 'plates', 'creations')
 * @param options - Metadata including source tracking
 */
export async function uploadPhoto(
  userId: string,
  file: File,
  folder: string = 'items',
  options?: PhotoUploadOptions
): Promise<PhotoUploadResult> {
  try {
    // Step 1: Compress the image first (so we know actual upload size)
    const compressedFile = await compressImage(file);
    const uploadSize = compressedFile.size;
    
    // Step 2: Check storage limits (unless skipped)
    if (!options?.skipStorageCheck) {
      const canUploadResult = await checkCanUpload(userId, uploadSize);
      
      if (!canUploadResult.allowed) {
        return {
          success: false,
          error: canUploadResult.reason || 'Storage limit reached',
          showUpgradeModal: true,
        };
      }
    }
    
    // Step 3: Upload to Firebase Storage
    const timestamp = Date.now();
    const safeName = compressedFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const storagePath = `users/${userId}/${folder}/${timestamp}_${safeName}`;
    const imageRef = ref(storage, storagePath);
    
    // Filename embeds a timestamp + safeName, so the URL is content-addressable.
    // Set Cache-Control so the CDN + downstream caches hold the photo for a
    // year instead of Firebase's default 1-hour private cache.
    await uploadBytes(imageRef, compressedFile, {
      cacheControl: 'public, max-age=31536000, immutable',
      contentType: 'image/jpeg',
    });
    const url = await getDownloadURL(imageRef);
    
    // Step 4: Track storage usage
    await addToStorageUsage(userId, uploadSize);
    
    // Step 5: Build photo object with provenance tracking
    const photo: PhotoWithProvenance = {
      url,
      isDefault: options?.isDefault || false,
      // Provenance tracking
      uploadedBy: userId,
      uploadedAt: new Date().toISOString(),
      source: options?.source || 'unknown',
      dmcaStatus: 'active',
    };
    
    // Optional fields
    if (options?.caption?.trim()) {
      photo.caption = options.caption.trim();
    }
    if (options?.creditName?.trim()) {
      photo.creditName = options.creditName.trim();
    }
    if (options?.creditUrl?.trim()) {
      photo.creditUrl = options.creditUrl.trim();
    }
    if (options?.sourceUrl?.trim()) {
      photo.sourceUrl = options.sourceUrl.trim();
    }
    if (options?.sourceUserId?.trim()) {
      photo.sourceUserId = options.sourceUserId.trim();
    }
    // Per-photo context — only persist non-empty. Firestore rejects
    // undefined, and empty strings are noise.
    if (options?.skinTone?.trim()) {
      photo.skinTone = options.skinTone.trim();
    }
    if (options?.lighting?.trim()) {
      photo.lighting = options.lighting.trim();
    }
    // Only stamp `photoRightsAttested: true` when explicitly attested. We
    // omit the field otherwise so legacy items grandfather cleanly (absence
    // === not-attested; also avoids writing `undefined` to Firestore).
    if (options?.attested === true) {
      photo.photoRightsAttested = true;
    }
    
    return {
      success: true,
      photo,
      bytesUsed: uploadSize,
    };
    
  } catch (error) {
    console.error('Photo upload failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Upload failed',
    };
  }
}

/**
 * Upload multiple photos with progress tracking
 * Checks total storage upfront before starting any uploads
 * 
 * @param userId - The user's ID
 * @param pendingPhotos - Array of photos to upload
 * @param folder - Storage folder (e.g., 'polishes', 'plates')
 * @param onProgress - Progress callback (0-100)
 * @param defaultSource - Default source if not specified per photo
 */
export async function uploadPhotos(
  userId: string,
  pendingPhotos: PendingPhoto[],
  folder: string = 'items',
  onProgress?: (progress: number) => void,
  defaultSource: PhotoSource = 'unknown',
  /** When true, stamps `photoRightsAttested: true` on every uploaded photo
   *  — the caller has confirmed the form-level rights checkbox is checked. */
  attested: boolean = false
): Promise<BulkUploadResult> {
  const result: BulkUploadResult = {
    success: true,
    photos: [],
    errors: [],
    totalBytesUsed: 0,
    failedCount: 0,
  };
  
  if (pendingPhotos.length === 0) {
    return result;
  }
  
  try {
    // Step 1: Estimate total size and check storage upfront
    const files = pendingPhotos.map(p => p.file);
    const estimatedSize = estimateUploadSize(files);
    
    const storageCheck = await checkStorageBeforeUpload(userId, estimatedSize);
    
    if (!storageCheck.canUpload) {
      return {
        success: false,
        photos: [],
        errors: [storageCheck.reason || 'Storage limit reached. Please upgrade or delete some photos.'],
        totalBytesUsed: 0,
        failedCount: pendingPhotos.length,
        storageBlocked: true,
      };
    }
    
    // Step 2: Upload each photo
    for (let i = 0; i < pendingPhotos.length; i++) {
      const pending = pendingPhotos[i];
      
      // Report progress
      if (onProgress) {
        onProgress(Math.round((i / pendingPhotos.length) * 100));
      }
      
      const uploadResult = await uploadPhoto(
        userId,
        pending.file,
        folder,
        {
          caption: pending.caption,
          creditName: pending.creditName,
          creditUrl: pending.creditUrl,
          isDefault: pending.isDefault,
          skipStorageCheck: true, // We already checked upfront
          // Source tracking
          source: pending.source || defaultSource,
          sourceUrl: pending.sourceUrl,
          sourceUserId: pending.sourceUserId,
          skinTone: pending.skinTone,
          lighting: pending.lighting,
          // Every photo in this batch shares the caller-attested flag; the
          // form-level rights checkbox governs all pending photos together.
          attested,
        }
      );
      
      if (uploadResult.success && uploadResult.photo) {
        result.photos.push(uploadResult.photo);
        result.totalBytesUsed += uploadResult.bytesUsed || 0;
      } else {
        result.errors.push(`Failed to upload ${pending.file.name}: ${uploadResult.error}`);
        result.failedCount++;
        
        if (uploadResult.showUpgradeModal) {
          result.storageBlocked = true;
          // Stop uploading if we hit storage limit mid-way
          break;
        }
      }
    }
    
    // Final progress
    if (onProgress) {
      onProgress(100);
    }
    
    // Mark as failed if any photos failed
    if (result.failedCount > 0) {
      result.success = false;
    }
    
    return result;
    
  } catch (error) {
    console.error('Bulk upload failed:', error);
    return {
      success: false,
      photos: result.photos, // Return any that succeeded
      errors: [...result.errors, error instanceof Error ? error.message : 'Upload failed'],
      totalBytesUsed: result.totalBytesUsed,
      failedCount: pendingPhotos.length - result.photos.length,
    };
  }
}

/**
 * Get the size of an already-uploaded photo (for tracking deletions)
 * 
 * @param photoUrl - The Firebase Storage URL of the photo
 */
export async function getPhotoSize(photoUrl: string): Promise<number> {
  try {
    // Extract the storage path from the URL
    const url = new URL(photoUrl);
    const pathMatch = url.pathname.match(/\/o\/(.+?)(\?|$)/);
    
    if (!pathMatch) {
      console.warn('Could not extract storage path from URL');
      return 0;
    }
    
    const storagePath = decodeURIComponent(pathMatch[1]);
    const imageRef = ref(storage, storagePath);
    const metadata = await getMetadata(imageRef);
    
    return metadata.size;
  } catch (error) {
    console.warn('Could not get photo size:', error);
    // Return estimate if we can't get actual size
    return 150 * 1024; // 150 KB average
  }
}

/**
 * Delete a photo from Firebase Storage and update storage usage.
 * Call this whenever a user removes a photo from an item.
 * Safely handles external photos (brand pages, web URLs etc.) by skipping
 * Storage deletion for anything not hosted in our Firebase Storage bucket.
 *
 * @param userId - The user's ID
 * @param photoUrl - The URL of the photo to delete
 */
export async function deletePhoto(
  userId: string,
  photoUrl: string
): Promise<void> {
  // Only delete from Firebase Storage if it's our hosted photo.
  // External photos (from web, brand pages etc.) don't need Storage deletion.
  const isFirebasePhoto = photoUrl.includes('firebasestorage.app') ||
                          photoUrl.includes('googleapis.com/storage');

  if (!isFirebasePhoto) {
    return;
  }

  try {
    // Get file size before deleting so we can subtract it from usage
    const fileSize = await getPhotoSize(photoUrl);

    // Extract storage path and delete the file
    const url = new URL(photoUrl);
    const pathMatch = url.pathname.match(/\/o\/(.+?)(\?|$)/);
    if (pathMatch) {
      const storagePath = decodeURIComponent(pathMatch[1]);
      const photoRef = ref(storage, storagePath);
      await deleteObject(photoRef);
    }

    // Subtract from storage usage count
    await subtractFromStorageUsage(userId, fileSize);

  } catch (error) {
    console.error('Error deleting photo from storage:', error);
    // Don't rethrow — we still want the photo removed from Firestore
    // even if Storage deletion fails
  }
}

/**
 * Check if a photo should be displayed (not hidden by DMCA)
 * Use this when rendering photos to respect DMCA status
 */
export function isPhotoVisible(photo: Photo | PhotoWithProvenance): boolean {
  const photoWithStatus = photo as PhotoWithProvenance;
  return !photoWithStatus.dmcaStatus || 
         photoWithStatus.dmcaStatus === 'active' || 
         photoWithStatus.dmcaStatus === 'cleared';
}

/**
 * Filter array of photos to only visible ones
 */
export function filterVisiblePhotos<T extends Photo>(photos: T[]): T[] {
  return photos.filter(isPhotoVisible);
}

/**
 * Helper to migrate existing modals to use this service
 * 
 * Replace this in your modal:
 * ```typescript
 * // OLD:
 * const uploadPhotos = async (userId: string): Promise<Photo[]> => {
 *   // ... your existing code
 * };
 * 
 * // NEW:
 * import { uploadPhotos as uploadPhotosService } from '../services/photoUploadService';
 * 
 * const handleUploadPhotos = async (userId: string): Promise<Photo[]> => {
 *   const result = await uploadPhotosService(
 *     userId, 
 *     pendingPhotos, 
 *     'polishes', 
 *     setUploadProgress,
 *     'original'  // Default source - user took the photo
 *   );
 *   
 *   if (result.storageBlocked) {
 *     setShowUpgradeModal(true);
 *     return [];
 *   }
 *   
 *   if (result.errors.length > 0) {
 *     console.warn('Some photos failed:', result.errors);
 *   }
 *   
 *   return result.photos;
 * };
 * ```
 */
